import React, { useState, useEffect } from 'react';
import { useLocation } from '../../contexts/LocationContext';
import './WarehouseSelector.css'; // Używam tych samych stylów

const LocationSelector = ({ onLocationChange }) => {
  const { selectedLocation, availableLocations, loading, error, isAdmin, changeLocation } = useLocation();
  const [showDropdown, setShowDropdown] = useState(false);

  const handleLocationChange = async (location) => {
    // changeLocation z kontekstu synchronizuje tez sesje backendu
    // (POST /auth/current-location)
    await changeLocation(location.id);
    setShowDropdown(false);
    if (onLocationChange) {
      onLocationChange(location);
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.4rem',
        padding: '0.4rem 0.75rem',
        backgroundColor: 'var(--bg)',
        borderRadius: '6px',
        fontSize: '0.8rem',
        color: 'var(--text-muted)'
      }}>
        <i className="fas fa-spinner fa-spin"></i>
        <span>Ładowanie...</span>
      </div>
    );
  }

  if (availableLocations.length === 0) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.4rem',
        padding: '0.4rem 0.75rem',
        backgroundColor: 'var(--warning-light)',
        borderRadius: '6px',
        fontSize: '0.8rem',
        color: 'var(--warning)'
      }}>
        <i className="fas fa-exclamation-triangle"></i>
        <span>Brak lokalizacji</span>
      </div>
    );
  }

  // Nie-admin: lokalizacja wybrana przy logowaniu jest STAŁA na czas sesji.
  // Zmiana wymaga wylogowania i ponownego zalogowania (backend i tak odrzuci
  // próbę zmiany 403) — chroni przed przypadkowym przełączeniem przez kasjera.
  if (!isAdmin && selectedLocation) {
    return (
      <div
        title="Lokalizacja jest zablokowana do końca sesji. Wyloguj się i zaloguj ponownie, aby ją zmienić."
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.4rem 0.75rem',
          backgroundColor: 'var(--bg)',
          border: '1px solid var(--border)',
          borderRadius: '6px',
          fontSize: '0.8rem',
          cursor: 'default'
        }}
      >
        <i className="fas fa-map-marker-alt" style={{ color: 'var(--info)', fontSize: '0.85rem' }}></i>
        <span style={{ color: 'var(--text)', fontWeight: '500', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selectedLocation.nazwa}
        </span>
        <i className="fas fa-lock" style={{ color: 'var(--text-muted)', fontSize: '0.7rem' }}></i>
      </div>
    );
  }

  // Jeśli użytkownik ma tylko jedną lokalizację, pokaż tylko nazwę (bez możliwości zmiany)
  if (availableLocations.length === 1) {
    return (
      <div style={{ 
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.4rem 0.75rem',
        backgroundColor: 'var(--success-light)',
        borderRadius: '6px',
        fontSize: '0.8rem'
      }}>
        <i className="fas fa-map-marker-alt" style={{ color: 'var(--success)', fontSize: '0.85rem' }}></i>
        <span style={{ color: 'var(--text)', fontWeight: '500' }}>
          {selectedLocation ? selectedLocation.nazwa : availableLocations[0].nazwa}
        </span>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.4rem 0.75rem',
          border: '1px solid var(--border)',
          borderRadius: '6px',
          backgroundColor: showDropdown ? 'var(--card-bg-hover)' : 'var(--bg)',
          cursor: 'pointer',
          fontSize: '0.8rem',
          fontWeight: '500',
          color: 'var(--text)',
          transition: 'all 0.15s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'var(--card-bg-hover)';
          e.currentTarget.style.borderColor = 'var(--primary)';
        }}
        onMouseLeave={(e) => {
          if (!showDropdown) {
            e.currentTarget.style.backgroundColor = 'var(--bg)';
            e.currentTarget.style.borderColor = 'var(--border)';
          }
        }}
      >
        <i className="fas fa-map-marker-alt" style={{ color: 'var(--info)', fontSize: '0.85rem' }}></i>
        <span style={{ maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selectedLocation ? selectedLocation.nazwa : 'Wybierz'}
        </span>
        <i 
          className={`fas fa-chevron-${showDropdown ? 'up' : 'down'}`} 
          style={{ 
            color: 'var(--text-muted)',
            fontSize: '0.7rem',
            transition: 'transform 0.15s ease'
          }}
        ></i>
      </button>

      {showDropdown && (
        <>
          {/* Overlay do zamykania dropdown */}
          <div 
            onClick={() => setShowDropdown(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999
            }}
          />
          <div 
            style={{
              position: 'absolute',
              top: 'calc(100% + 4px)',
              right: 0,
              minWidth: '200px',
              backgroundColor: 'var(--card-bg)',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.4)',
              zIndex: 1000,
              maxHeight: '280px',
              overflowY: 'auto'
            }}
          >
            <div style={{
              padding: '0.5rem 0.75rem',
              fontSize: '0.7rem',
              color: 'var(--text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              borderBottom: '1px solid var(--border)'
            }}>
              Wybierz lokalizację
            </div>
            {availableLocations.map((location) => (
              <button
                key={location.id}
                onClick={() => handleLocationChange(location)}
                style={{
                  width: '100%',
                  padding: '0.6rem 0.75rem',
                  
                  backgroundColor: selectedLocation?.id === location.id
                    ? 'var(--info-light)'
                    : 'transparent',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontSize: '0.8rem',
                  transition: 'background-color 0.15s ease',
                  borderBottom: '1px solid var(--border)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = selectedLocation?.id === location.id
                    ? 'var(--info-light)'
                    : 'var(--bg)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = selectedLocation?.id === location.id
                    ? 'var(--info-light)'
                    : 'transparent';
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: '600', color: 'var(--text)' }}>
                      {location.nazwa}
                    </div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '2px' }}>
                      {location.miasto || location.kod_lokalizacji}
                    </div>
                  </div>
                  {selectedLocation?.id === location.id && (
                    <i className="fas fa-check" style={{ color: 'var(--info)', fontSize: '0.75rem' }}></i>
                  )}
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default LocationSelector;
